import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { YtThumb } from "@/components/RecommendedVideos";
import { GlobalAddVideoModal } from "@/components/GlobalAddVideoModal";
import { ProjectPicker } from "@/components/ProjectPicker";
import { useAuth } from "@/auth/AuthContext";
import { Video, Link2, Search, ExternalLink, Star, CalendarDays, Library, Sparkles, Plus, Pencil, CheckCircle2, Loader2, X, Archive, Undo2, ChevronDown, Users } from "lucide-react";
import { normalizeUrl } from "@/lib/normalizeUrl";

// Fix 1 (2026-02 post-deployment): standalone Video Library page at /videos — the module
// registry has always had a "Recommended Videos" nav item (videos.py's MANIFEST), but until
// now it pointed nowhere (the ONLY consumer was the per-project embedded panel on
// ProjectDashboard.jsx, RecommendedVideos.jsx, which stays exactly as-is). This page browses
// the WHOLE Master Library + every External Reference Link across every project — read-only
// curation happens per-project via that embedded panel, not here.
//
// Deliverables Phase 3 (2026-08): the pool here also includes portfolio-flagged Films &
// Deliverables (via `include_portfolio=1`, opt-in on this page only — the per-project attach
// picker calls the SAME /v1/videos/library endpoint WITHOUT this param, so it's unaffected).
// The Master Library itself is never hard-cut — this only adds to the pool.

// Video Library Curation Part 1 (2026-08): de-dupe + re-order. The backend now returns
// {portfolio, master_library, overlap_count} instead of one flat merged list — a video that
// exists in both is folded into ONE portfolio card (never shown twice), and the two are
// rendered as their own distinct sections, Portfolio first. The pre-existing "Portfolio only"
// checkbox is retired: Portfolio now has its own always-visible section, so the toggle would
// just duplicate what that section already shows.

// Video Library Curation Part 2/3 (2026-08): in-place curation directly on a card — Edit
// (title/description/category/tags), Portfolio toggle, Link to project(s), and multi-select
// bulk-link. A Master-Library-only card has no Films & Deliverables record yet, so its FIRST
// curation action (whichever one — edit, portfolio, or link) PROMOTES it by calling the exact
// same upsert-by-media-id endpoint Add Video already uses (`POST /v1/deliverables`) — never a
// parallel "promote" endpoint. Once promoted, later actions PATCH that record directly
// (`PATCH /v1/deliverables/{id}`, the project-less sibling of the Deliverables tab's own PATCH).

const EditForm = ({ v, categoryOptions, tagOptions, onCancel, onSave }) => {
  const [title, setTitle] = useState(v.name || "");
  const [description, setDescription] = useState(v.description || "");
  // Master Library rows use free-form categories (e.g. "NJM Internal"), not the fixed 9-option
  // Films & Deliverables list — only pre-select if it's a real match, otherwise leave blank
  // and let the backend's own category_hint fallback (passed separately) do the closest-match.
  const [category, setCategory] = useState(categoryOptions.includes(v.category) ? v.category : "");
  const [tags, setTags] = useState(v.tags || []);
  const [saving, setSaving] = useState(false);
  const toggleTag = (t) => setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const save = async () => {
    setSaving(true);
    await onSave({ title: title.trim(), description: description.trim(), category: category || undefined, tags });
    setSaving(false);
  };

  return (
    <div className="p-2.5 space-y-1.5" data-testid={`video-library-edit-form-${v.id}`}>
      <input data-testid={`video-library-edit-title-${v.id}`} value={title} onChange={(e) => setTitle(e.target.value)}
        className="w-full rounded-md border border-hairline bg-paper px-2 py-1 text-xs focus:outline-none focus:border-brand/50" />
      <textarea data-testid={`video-library-edit-description-${v.id}`} value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
        className="w-full rounded-md border border-hairline bg-paper px-2 py-1 text-xs focus:outline-none focus:border-brand/50 resize-none" />
      <select data-testid={`video-library-edit-category-${v.id}`} value={category} onChange={(e) => setCategory(e.target.value)}
        className="w-full rounded-md border border-hairline bg-paper px-2 py-1 text-xs focus:outline-none focus:border-brand/50">
        <option value="">Select category…</option>
        {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      {tagOptions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {tagOptions.map((t) => (
            <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`video-library-edit-tag-${t}-${v.id}`}
              className={`rounded-full border px-1.5 py-0.5 text-[9px] ${tags.includes(t) ? "border-brand bg-brand/15 text-ink" : "border-hairline text-faint"}`}>{t}</button>
          ))}
        </div>
      )}
      <div className="flex gap-1.5 pt-0.5">
        <button onClick={save} disabled={saving} data-testid={`video-library-edit-save-${v.id}`}
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-ink px-2 py-1 text-[10px] text-white hover:bg-brand disabled:opacity-50">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Save
        </button>
        <button onClick={onCancel} data-testid={`video-library-edit-cancel-${v.id}`}
          className="rounded-md border border-hairline px-2 py-1 text-[10px] text-faint hover:border-brand">Cancel</button>
      </div>
    </div>
  );
};

const VideoCard = ({ v, canWrite, categoryOptions, tagOptions, selected, onToggleSelect, onSave, onTogglePortfolio, onLink, onToggleAgencyVisible }) => {
  const [editing, setEditing] = useState(false);
  const [linking, setLinking] = useState(false);
  const isPortfolio = v.source === "deliverable";

  return (
    <div className="group rounded-md border border-hairline overflow-hidden hover:border-brand/50 transition-colors relative" data-testid={`video-library-page-card-${v.id}`}>
      {canWrite && (
        <input type="checkbox" checked={!!selected} onChange={() => onToggleSelect(v.id)} data-testid={`video-library-select-${v.id}`}
          className="absolute top-1.5 left-1.5 z-10 h-3.5 w-3.5 rounded border-white/60 accent-brand" />
      )}
      {isPortfolio && (
        <span data-testid={`video-library-portfolio-badge-${v.id}`} className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-0.5 rounded-full bg-brand/90 px-1.5 py-0.5 text-[9px] text-white">
          <Sparkles className="h-2.5 w-2.5" /> Portfolio
        </span>
      )}
      {!isPortfolio && v.agency_visible && (
        <span data-testid={`video-library-agency-visible-badge-${v.id}`} className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-0.5 rounded-full bg-emerald-600/90 px-1.5 py-0.5 text-[9px] text-white">
          <Users className="h-2.5 w-2.5" /> Agency
        </span>
      )}
      <a href={normalizeUrl(v.video_url)} target="_blank" rel="noopener noreferrer" className="block">
        <YtThumb url={v.video_url} className="w-full aspect-video" />
      </a>
      {editing ? (
        <EditForm v={v} categoryOptions={categoryOptions} tagOptions={tagOptions} onCancel={() => setEditing(false)}
          onSave={async (patch) => { await onSave(v, patch); setEditing(false); }} />
      ) : (
        <div className="p-2.5">
          <a href={normalizeUrl(v.video_url)} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-ink line-clamp-2 hover:text-brand transition-colors">{v.name}</a>
          <div className="mt-1.5 flex items-center gap-2 text-[10px] text-faint flex-wrap">
            {isPortfolio ? (
              v.master_library_fallback && <span data-testid={`video-library-master-fallback-${v.id}`} className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 text-brand" /> {v.master_library_fallback.recommendation_score ?? 0}</span>
            ) : (
              <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 text-brand" /> {v.recommendation_score ?? 0}</span>
            )}
            {v.date_published && <span className="inline-flex items-center gap-0.5"><CalendarDays className="h-3 w-3" /> {v.date_published}</span>}
            <span className="truncate">{v.category}</span>
          </div>
          {canWrite && (
            <div className="mt-2 flex items-center gap-1 border-t border-hairline pt-1.5 relative">
              <button onClick={() => setEditing(true)} data-testid={`video-library-edit-btn-${v.id}`} title="Edit" className="text-faint hover:text-brand"><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => onTogglePortfolio(v)} data-testid={`video-library-portfolio-toggle-${v.id}`} title={isPortfolio ? "Remove from portfolio" : "Add to portfolio"}
                className={isPortfolio ? "text-brand" : "text-faint hover:text-brand"}><Star className="h-3.5 w-3.5" fill={isPortfolio ? "currentColor" : "none"} /></button>
              <button onClick={() => setLinking((s) => !s)} data-testid={`video-library-link-btn-${v.id}`} title="Link to project" className="text-faint hover:text-brand"><Link2 className="h-3.5 w-3.5" /></button>
              {!isPortfolio && (
                <button onClick={() => onToggleAgencyVisible(v)} data-testid={`video-library-agency-visible-toggle-${v.id}`} title={v.agency_visible ? "Hide from Agency Portal Archives" : "Show in Agency Portal Archives"}
                  className={v.agency_visible ? "text-emerald-600" : "text-faint hover:text-emerald-600"}><Users className="h-3.5 w-3.5" fill={v.agency_visible ? "currentColor" : "none"} /></button>
              )}
              {linking && (
                <ProjectPicker onClose={() => setLinking(false)} submitLabel="Link"
                  onSubmit={async (ids) => { await onLink(v, ids); setLinking(false); }} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function VideoLibrary() {
  const { hasScope } = useAuth();
  const canWrite = hasScope("films_deliverables.write");
  const [portfolioVideos, setPortfolioVideos] = useState([]);
  const [masterVideos, setMasterVideos] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [videoQuery, setVideoQuery] = useState("");
  const [category, setCategory] = useState("");
  const [addVideoOpen, setAddVideoOpen] = useState(false);
  const [deliverableCategoryOptions, setDeliverableCategoryOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLinking, setBulkLinking] = useState(false);

  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [linkQuery, setLinkQuery] = useState("");

  // Batch polish (2026-08): archived rows were previously invisible everywhere — fetched
  // lazily (only once the section is expanded) since it's an occasional moderation check, not
  // part of the normal page load.
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archivedVideos, setArchivedVideos] = useState(null);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const loadArchived = () => {
    setArchivedLoading(true);
    api.get("/v1/videos/library/archived").then((r) => setArchivedVideos(r.data.items || [])).catch(() => setArchivedVideos([])).finally(() => setArchivedLoading(false));
  };
  const toggleArchived = () => {
    const next = !archivedOpen;
    setArchivedOpen(next);
    if (next && archivedVideos === null) loadArchived();
  };
  const handleRestore = async (v) => {
    setRestoringId(v.id);
    try {
      await api.post(`/v1/videos/library/${v.id}/unarchive`);
      toast.success(`"${v.name}" restored to Active`);
      setArchivedVideos((prev) => prev.filter((x) => x.id !== v.id));
      loadVideos();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "Couldn't restore this video"); }
    finally { setRestoringId(null); }
  };

  // One unfiltered snapshot on mount, purely to derive the category dropdown's live options —
  // avoids a second backend endpoint just for select-options.
  useEffect(() => {
    api.get("/v1/videos/library?include_portfolio=1").then((r) => setAllVideos([...(r.data.portfolio || []), ...(r.data.master_library || [])])).catch(() => {});
    if (canWrite) {
      api.get("/v1/deliverables/category-options").then((r) => setDeliverableCategoryOptions(r.data.category_options || [])).catch(() => {});
      api.get("/v1/deliverables/tag-options").then((r) => setTagOptions(r.data.tag_options || [])).catch(() => {});
    }
  }, [canWrite]);
  const categoryOptions = useMemo(() => [...new Set(allVideos.map((v) => v.category).filter(Boolean))].sort(), [allVideos]);

  const loadVideos = () => {
    setVideosLoading(true);
    const params = new URLSearchParams({ include_portfolio: "1" });
    if (category) params.set("category", category);
    if (videoQuery) params.set("q", videoQuery);
    api.get(`/v1/videos/library?${params.toString()}`)
      .then((r) => { setPortfolioVideos(r.data.portfolio || []); setMasterVideos(r.data.master_library || []); })
      .catch(() => { setPortfolioVideos([]); setMasterVideos([]); })
      .finally(() => setVideosLoading(false));
  };
  useEffect(() => { const t = setTimeout(loadVideos, 200); return () => clearTimeout(t); }, [category, videoQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLinks = () => {
    setLinksLoading(true);
    const params = new URLSearchParams();
    if (linkQuery) params.set("q", linkQuery);
    api.get(`/v1/videos/external-links?${params.toString()}`).then((r) => setLinks(r.data.items || [])).catch(() => setLinks([])).finally(() => setLinksLoading(false));
  };
  useEffect(() => { const t = setTimeout(loadLinks, 200); return () => clearTimeout(t); }, [linkQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Video Library Curation Part 2 (2026-08): promote-on-first-edit — a Master-Library-only
  // card has no Films & Deliverables record yet, so its first curation action calls the exact
  // same upsert-by-media-id endpoint Add Video uses; once promoted, later actions PATCH it
  // directly. Either way: reload afterward so the card re-renders in its (possibly new)
  // section, backed by whichever record now actually exists.
  const handleSaveEdit = async (v, patch) => {
    try {
      if (v.source === "deliverable") {
        await api.patch(`/v1/deliverables/${v.deliverable_id}`, patch);
      } else {
        await api.post("/v1/deliverables", { title: patch.title, description: patch.description, category: patch.category, tags: patch.tags, final_url: v.video_url, category_hint: v.category, project_ids: [] });
      }
      toast.success("Saved");
      loadVideos();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "Couldn't save this edit"); }
  };

  const handleTogglePortfolio = async (v) => {
    try {
      if (v.source === "deliverable") {
        await api.patch(`/v1/deliverables/${v.deliverable_id}`, { portfolio: false });
        toast.success("Removed from portfolio");
      } else {
        await api.post("/v1/deliverables", { title: v.name, final_url: v.video_url, category_hint: v.category, project_ids: [], portfolio: true, apply_portfolio_on_attach: true });
        toast.success("Added to portfolio");
      }
      loadVideos();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "Couldn't update portfolio flag"); }
  };

  const handleLink = async (v, projectIds) => {
    try {
      const r = await api.post("/v1/deliverables", { title: v.name, final_url: v.video_url, category_hint: v.category, project_ids: projectIds });
      toast.success(r.data.attached_existing ? "Linked to the selected project(s)" : "Added & linked to the selected project(s)");
      loadVideos();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "Couldn't link this video"); }
  };

  // Agency Portal PM Feedback Batch 2, item 3 (2026-09) — Master-Library-only toggle (a
  // portfolio row has no video_links id of its own to key the flag on).
  const handleToggleAgencyVisible = async (v) => {
    try {
      const r = await api.post(`/v1/videos/${v.id}/agency-visibility`, { visible: !v.agency_visible });
      toast.success(r.data.agency_visible ? "Now visible in the Agency Portal Archives" : "Hidden from the Agency Portal Archives");
      setMasterVideos((prev) => prev.map((x) => (x.id === v.id ? { ...x, agency_visible: r.data.agency_visible } : x)));
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "Couldn't update this video's agency visibility"); }
  };

  const toggleSelect = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleBulkLink = async (projectId) => {
    try {
      const r = await api.post("/v1/deliverables/bulk-link", { video_ids: selectedIds, project_id: projectId });
      const failed = (r.data.results || []).filter((x) => !x.ok);
      toast.success(failed.length ? `Linked ${r.data.results.length - failed.length} of ${r.data.results.length} — ${failed.length} failed` : `Linked ${r.data.results.length} video(s) to the project`);
      setSelectedIds([]);
      setBulkLinking(false);
      loadVideos();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "Bulk-link failed"); }
  };

  return (
    <div className="p-5 sm:p-10 space-y-8" data-testid="video-library-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-editorial text-4xl tracking-tight flex items-center gap-3">
            <Video className="h-8 w-8 text-brand" /> Video Library
          </h1>
          <p className="text-sm text-faint mt-1.5">The whole Recommended Videos Master Library + portfolio-flagged Films &amp; Deliverables + External Reference Links, across every project. Curate per-project from the project's own detail page.</p>
        </div>
        {canWrite && (
          <button onClick={() => setAddVideoOpen(true)} data-testid="video-library-add-video-btn"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-2 text-xs text-white hover:bg-brand transition-colors">
            <Plus className="h-3.5 w-3.5" /> Add Video
          </button>
        )}
      </div>

      {selectedIds.length > 0 && (
        <div data-testid="video-library-bulk-bar" className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-md border border-brand/40 bg-brand/10 px-4 py-2.5">
          <span className="text-xs text-ink">{selectedIds.length} video(s) selected</span>
          <div className="relative flex items-center gap-2">
            <button onClick={() => setBulkLinking((s) => !s)} data-testid="video-library-bulk-link-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-1.5 text-[11px] text-white hover:bg-brand"><Link2 className="h-3 w-3" /> Link to project</button>
            <button onClick={() => setSelectedIds([])} data-testid="video-library-bulk-clear-btn" className="text-faint hover:text-ink"><X className="h-4 w-4" /></button>
            {bulkLinking && <ProjectPicker multiple={false} submitLabel="Link all" onClose={() => setBulkLinking(false)} onSubmit={handleBulkLink} />}
          </div>
        </div>
      )}

      <section className="rounded-lg border border-hairline bg-paper" data-testid="video-library-portfolio-section">
        <header className="border-b border-hairline px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="text-xs font-semibold uppercase tracking-wider text-faint flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-brand" /> Portfolio ({portfolioVideos.length})</div>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-faint" />
              <input data-testid="video-library-page-search" value={videoQuery} onChange={(e) => setVideoQuery(e.target.value)} placeholder="Search title…"
                className="rounded-md border border-hairline bg-paper pl-8 pr-3 py-2 text-sm w-56 focus:border-brand outline-none transition-colors" />
            </div>
            <select data-testid="video-library-page-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
              className="rounded-md border border-hairline bg-paper px-3 py-2 text-sm focus:border-brand outline-none transition-colors">
              <option value="">All categories</option>
              {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </header>
        <div className="p-5">
          {videosLoading ? (
            <div className="text-sm text-faint text-center py-8">Loading…</div>
          ) : portfolioVideos.length === 0 ? (
            <EmptyState title="No portfolio videos yet" hint="Flag a Films & Deliverables entry as Portfolio-worthy from a project's Deliverables tab." testId="video-library-portfolio-empty" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {portfolioVideos.map((v) => (
                <VideoCard key={v.id} v={v} canWrite={canWrite} categoryOptions={deliverableCategoryOptions} tagOptions={tagOptions}
                  selected={selectedIds.includes(v.id)} onToggleSelect={toggleSelect} onSave={handleSaveEdit} onTogglePortfolio={handleTogglePortfolio} onLink={handleLink} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-hairline bg-paper" data-testid="video-library-master-section">
        <header className="border-b border-hairline px-5 py-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-faint flex items-center gap-1.5"><Library className="h-3.5 w-3.5" /> Master Library ({masterVideos.length})</div>
        </header>
        <div className="p-5">
          {videosLoading ? (
            <div className="text-sm text-faint text-center py-8">Loading…</div>
          ) : masterVideos.length === 0 ? (
            <EmptyState title="No videos match this filter" hint="Try a different search or category." testId="video-library-master-empty" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {masterVideos.map((v) => (
                <VideoCard key={v.id} v={v} canWrite={canWrite} categoryOptions={deliverableCategoryOptions} tagOptions={tagOptions}
                  selected={selectedIds.includes(v.id)} onToggleSelect={toggleSelect} onSave={handleSaveEdit} onTogglePortfolio={handleTogglePortfolio} onLink={handleLink} onToggleAgencyVisible={handleToggleAgencyVisible} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-hairline bg-paper" data-testid="video-library-external-section">
        <header className="border-b border-hairline px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="text-xs font-semibold uppercase tracking-wider text-faint flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> External Reference Links ({links.length})</div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-faint" />
            <input data-testid="video-library-external-search" value={linkQuery} onChange={(e) => setLinkQuery(e.target.value)} placeholder="Search title…"
              className="rounded-md border border-hairline bg-paper pl-8 pr-3 py-2 text-sm w-56 focus:border-brand outline-none transition-colors" />
          </div>
        </header>
        <div className="divide-y divide-hairline">
          {linksLoading ? (
            <div className="text-sm text-faint text-center py-8">Loading…</div>
          ) : links.length === 0 ? (
            <EmptyState title="No external reference links yet" hint="Added from a project's Recommended Videos panel." testId="video-library-external-empty" />
          ) : (
            links.map((l) => (
              <a key={l.id} href={normalizeUrl(l.link)} target="_blank" rel="noopener noreferrer" data-testid={`video-library-external-row-${l.id}`}
                className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-surface2 transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-ink truncate inline-flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5 text-faint shrink-0" /> {l.name} <ExternalLink className="h-3 w-3 text-faint shrink-0" /></div>
                  <div className="text-[11px] text-faint mt-0.5">{[l.source_channel, l.project_names?.join(", "), l.date_added].filter(Boolean).join(" · ") || "—"}</div>
                </div>
              </a>
            ))
          )}
        </div>
      </section>
      {canWrite && (
        <section className="rounded-lg border border-hairline bg-paper" data-testid="video-library-archived-section">
          <button onClick={toggleArchived} data-testid="video-library-archived-toggle"
            className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-surface2/40 transition-colors">
            <div className="text-xs font-semibold uppercase tracking-wider text-faint flex items-center gap-1.5">
              <Archive className="h-3.5 w-3.5" /> Archived{archivedVideos !== null ? ` (${archivedVideos.length})` : ""}
            </div>
            <ChevronDown className={`h-4 w-4 text-faint transition-transform ${archivedOpen ? "rotate-180" : ""}`} />
          </button>
          {archivedOpen && (
            <div className="border-t border-hairline divide-y divide-hairline">
              {archivedLoading ? (
                <div className="text-sm text-faint text-center py-8">Loading…</div>
              ) : !archivedVideos?.length ? (
                <EmptyState title="Nothing archived" hint="Rows archived via Master Library Cleanup (or manually) show up here, fully restorable." testId="video-library-archived-empty" />
              ) : (
                archivedVideos.map((v) => (
                  <div key={v.id} data-testid={`video-library-archived-row-${v.id}`} className="flex items-center justify-between gap-3 px-5 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-ink truncate">{v.name}</div>
                      <div className="text-[11px] text-faint mt-0.5">{[v.category, v.video_url].filter(Boolean).join(" · ") || "—"}</div>
                    </div>
                    <button onClick={() => handleRestore(v)} disabled={restoringId === v.id} data-testid={`video-library-restore-btn-${v.id}`}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-hairline px-3 py-1.5 text-[11px] text-ink hover:border-brand hover:bg-brand/10 transition-colors disabled:opacity-50">
                      {restoringId === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Restore
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </section>
      )}
      {addVideoOpen && (
        <GlobalAddVideoModal onClose={() => setAddVideoOpen(false)} onAdded={() => { setAddVideoOpen(false); loadVideos(); }} />
      )}
    </div>
  );
}
